import { useSyncExternalStore } from 'react';

// Theme for the new design system: light / dark / gameboy (a DMG-green skin of the
// same screens). Defaults to the OS light/dark on first run, then remembers the
// person's explicit choice. Written onto `.tp-root` as `data-mode` (read by the
// tokens) and mirrored onto `<html data-tp>` for the full-bleed body reset.
//
// A module-level store (not per-component useState) so every consumer — Shell, the
// ThemeSwitch, Landing — shares ONE mode. Otherwise the switch would change its own
// copy while the wrapper's data-mode stayed put.
export type Mode = 'light' | 'dark' | 'gameboy';
export const MODES: Mode[] = ['light', 'dark', 'gameboy'];
// Game Boy is temporarily hidden from the live theme switcher (the skin + all its
// code stay put — flip this back to true to bring it back). AVAILABLE_MODES is what
// the UI offers; read() moves anyone stuck on gameboy back to a live theme.
export const GAMEBOY_ENABLED = false;
export const AVAILABLE_MODES: Mode[] = GAMEBOY_ENABLED ? MODES : ['light', 'dark'];
const KEY = 'tpiap:mode';

function osDefault(): Mode {
  try {
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

function read(): Mode {
  try {
    const s = localStorage.getItem(KEY);
    if (s === 'light' || s === 'dark') return s;
    if (s === 'gameboy') return GAMEBOY_ENABLED ? 'gameboy' : osDefault();
  } catch {
    /* ignore */
  }
  return osDefault();
}

let mode: Mode = read();
const listeners = new Set<() => void>();
let subscribers = 0;

function applyFlag() {
  try {
    document.documentElement.dataset.tp = mode;
  } catch {
    /* ignore */
  }
}

export function setMode(next: Mode) {
  if (next === mode) return;
  mode = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    /* ignore */
  }
  applyFlag();
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  subscribers += 1;
  applyFlag(); // a redesign surface is on screen → own the page
  return () => {
    listeners.delete(cb);
    subscribers -= 1;
    if (subscribers === 0) {
      try {
        delete document.documentElement.dataset.tp; // last redesign surface gone → give legacy routes their frame back
      } catch {
        /* ignore */
      }
    }
  };
}

export function useTheme() {
  const m = useSyncExternalStore(subscribe, () => mode, () => mode);
  const toggle = () => {
    const i = AVAILABLE_MODES.indexOf(mode);
    setMode(AVAILABLE_MODES[(i + 1) % AVAILABLE_MODES.length] ?? AVAILABLE_MODES[0]);
  };
  return { mode: m, setMode, toggle };
}
